import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Empty, Input, Popover } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import style from './index.scss';
import { EVENT_CATEGORY } from './constants';

/**
 * "+ Add fields from schema" — the picker behind the design's blue button.
 *
 * Story §8: the list comes from the Fraud Prevention API schema and the UI must
 * NOT keep its own. So `availableFields` is passed in; this component never
 * invents a field. Anything not offered by the schema cannot be configured, which
 * is the rule that stops a config naming a payload key that will never arrive.
 *
 * MULTI-SELECT, because the button is plural and configuring a type means picking
 * several fields at once; one-at-a-time would mean reopening it six times.
 */

/** Matches against BOTH names — §8 requires searching either. */
const matches = (field, term) => {
  if (!term) return true;
  const q = term.trim().toLowerCase();
  return (
    field.displayName.toLowerCase().includes(q) ||
    field.technicalName.toLowerCase().includes(q)
  );
};

const AddFieldsModal = ({
  children,
  open,
  category,
  availableFields,
  configuredKeys,
  onAdd,
  onCancel
}) => {
  const [term, setTerm] = useState('');

  // Already-configured fields are REMOVED, not shown disabled: the story forbids
  // adding a duplicate, and a list of greyed rows the admin cannot use is mostly
  // noise in a list this long (137 transaction fields).
  const results = useMemo(() => {
    /* MATCHED ON `fieldKey`, NOT `technicalName`.
       `configuredKeys` is `fields.map(f => f.fieldKey)` — what the config
       actually stores — so comparing it against `technicalName` only worked
       where the two columns happen to agree. They always do for activities
       (`ip_address` -> `ip_address`), which is why this went unnoticed; they
       differ for several transaction fields:
           fieldKey         technicalName
           txn_type         transaction_type_description
           receiver_type    receiver_party_type
           transaction_id   id
       Measured on XBORDER (2026-09-03): 3 of its 8 configured fields were still
       offered in the picker. Adding one again inserts a SECOND
       fp_event_type_field row for the same field — the table has no unique key
       on (event_type_id, field_key), so it persists, and the field then renders
       twice with two independent sets of ticks. */
    const taken = new Set(configuredKeys);
    /* `?? technicalName` keeps the FIXTURE path working. `__fixtures__/
       availableFields.js` predates the endpoint and carries only
       `technicalName` / `displayName`, and index.js uses it RAW on a 404 —
       not through toAvailableFields. Matching `fieldKey` alone there would
       compare against undefined, filter nothing, and offer every configured
       field back. For the fixture the two names are the same string anyway. */
    const identity = (f) => f.fieldKey ?? f.technicalName;
    return availableFields
      .filter((f) => !taken.has(identity(f)))
      .filter((f) => matches(f, term));
  }, [availableFields, configuredKeys, term]);

  // Only the search term survives a pick, and it is cleared so reopening the
  // picker starts fresh rather than showing the last query's results.
  const reset = () => setTerm('');

  /**
   * ADDS ON CLICK — no checkboxes, no Cancel/Add footer (design, 2026-08-31).
   *
   * The design's picker is a search-and-pick list: one click adds the field and
   * closes. The multi-select modal that was here made the common case — add one
   * field — three actions instead of one.
   *
   * Still hands back a whole schema ENTRY rather than a key: the caller needs
   * `displayName` to seed the editable label (§9: the default display name comes
   * from the schema) and `isIdentifier` to know what it must not let go of.
   */
  const pick = (field) => {
    onAdd([field]);
    reset();
  };

  const close = () => {
    reset();
    onCancel();
  };

  return (
    <Popover
      /* A POPOVER, NOT A MODAL. The design anchors this under the button —
         `data-radix-popper-content-wrapper` with `data-side="bottom"
         data-align="start"`, `w-96 p-3`, measured 384 x 322 — where ours
         centred a masked dialog over the page. Picking a field is a small,
         local act next to the list it adds to; a full-screen mask for it
         overstated the interaction and hid the table you are filling.
         No title and no footer either: the design has neither, and dismissal
         is a click outside, which antd gives us. */
      overlayClassName={style.fieldsPopover}
      open={open}
      onOpenChange={(next) => (next ? undefined : close())}
      trigger="click"
      placement="bottomLeft"
      destroyTooltipOnHide
      content={
        /* OUR OWN PADDED BOX, not antd's. Its `-inner-content` ships
           `width: max-content` and `padding: 12px 16px`, and negotiating with
           both from outside kept producing either a shrink-wrapped panel or
           rows that escaped the padding. Zeroing antd's and owning the box here
           makes the 12px (`p-3`) and the 384px (`w-96`) deterministic. */
        <div className={style.pickerBody}>
          <Input
            allowClear
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            prefix={<SearchOutlined />}
            className={style.pickerSearch}
            placeholder="Search schema fields…"
            aria-label={`Search ${
              category === EVENT_CATEGORY.ACTIVITY ? 'activity' : 'transaction'
            } schema fields`}
          />

          <div className={style.pickerList}>
            {results.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  term
                    ? 'No matching fields'
                    : 'Every available field is already configured'
                }
              />
            ) : (
              results.map((field) => (
                <button
                  type="button"
                  className={style.pickerRow}
                  /* `fieldKey` is the identity here — it is what the config
                 stores, what toAvailableFields dedupes on, and what the filter
                 above now matches. `technicalName` happens to be unique across
                 the 45 transaction fields today, but keying on it would break
                 silently the day two API names map to one column. */
                  key={field.fieldKey ?? field.technicalName}
                  onClick={() => pick(field)}
                >
                  <span className={style.pickerText}>
                    <span className={style.pickerName}>
                      {field.displayName}
                    </span>
                    {/* §8: "each result should show both the display name and the
                    technical/API field key" — the key is what will be matched
                    against the payload, so it is never hidden. */}
                    <span className={style.pickerKey}>
                      {field.technicalName}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      }
    >
      {children}
    </Popover>
  );
};

AddFieldsModal.propTypes = {
  /** The trigger the popover anchors to — the blue "Add fields" button. */
  children: PropTypes.node,
  open: PropTypes.bool,
  category: PropTypes.oneOf(Object.values(EVENT_CATEGORY)).isRequired,
  /** `SchemaFieldEntry[]` for this category. */
  availableFields: PropTypes.arrayOf(
    PropTypes.shape({
      /** The identity: what the config stores, and what is matched against
          `configuredKeys`. NOT interchangeable with technicalName. */
      /* Optional only because the pre-endpoint fixture omits it; the live
         adapter always sets it. */
      fieldKey: PropTypes.string,
      technicalName: PropTypes.string.isRequired,
      displayName: PropTypes.string,
      isIdentifier: PropTypes.bool
    })
  ),
  /** `fieldKey`s already on the event type — filtered out of the results. */
  configuredKeys: PropTypes.arrayOf(PropTypes.string),
  /** Called with the chosen schema entries. */
  onAdd: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

AddFieldsModal.defaultProps = {
  open: false,
  availableFields: [],
  configuredKeys: []
};

export default AddFieldsModal;
